<template>
  <div class="solution-detail">
    <!-- 顶部标题区域 -->
    <section class="products-header">
      <div class="container">
        <h1 class="page-title">
          <span class="title-glow">网络安全详情</span>
        </h1>
        <p class="page-subtitle">深入了解我们的网络安全解决方案与服务能力</p>
      </div>
    </section>

    <!-- 主内容区域 -->
    <section class="detail-main">
      <div class="container">
        <!-- 返回按钮 -->
        <div class="back-button">
          <button @click="goBack" class="btn-back">
            <span>←</span> 返回网络安全
          </button>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="detail-loading">
          <div class="loading-spinner"></div>
          <p>加载中...</p>
        </div>
        <!-- 错误提示 -->
        <div v-else-if="error" class="detail-error">
          <p>{{ error }}</p>
          <p class="error-hint">请确保 Node.js 服务器正在运行（端口 3000）</p>
        </div>
        <!-- 解决方案详情卡片 -->
        <div v-else-if="solutionDetail" class="detail-card">
          <!-- 方案标题 -->
          <div class="detail-header">
            <h2 class="detail-title">{{ solutionTitle }}</h2>
            <div class="detail-meta">
              <span class="detail-category">{{ solutionCategory }}</span>
              <span class="detail-date" v-if="updateDate">更新日期: {{ updateDate }}</span>
            </div>
          </div>

          <!-- 方案主图 -->
          <div class="detail-image" v-if="solutionImage">
            <img :src="solutionImage" :alt="solutionTitle" />
          </div>

          <!-- 方案简介 -->
          <div class="detail-intro" v-if="solutionDescription">
            <h3 class="intro-title">方案简介</h3>
            <p class="intro-content">
              {{ solutionDescription }}
            </p>
          </div>

          <!-- 方案特点 -->
          <div class="detail-features" v-if="features && features.length > 0">
            <h3 class="features-title">方案特点</h3>
            <div class="features-content">
              <ul class="features-list">
                <li v-for="(feature, index) in features" :key="index">
                  {{ feature }}
                </li>
              </ul>
            </div>
          </div>

          <!-- 应用场景 -->
          <div class="detail-scenarios" v-if="scenarios && scenarios.length > 0">
            <h3 class="scenarios-title">应用场景</h3>
            <div class="scenarios-content">
              <p>{{ scenariosIntro }}</p>
              <ul class="scenarios-list">
                <li v-for="(scenario, index) in scenarios" :key="index">
                  {{ scenario }}
                </li>
              </ul>
            </div>
          </div>

          <!-- 技术栈/实施流程 -->
          <!-- <div class="detail-process" v-if="processSteps && processSteps.length > 0">
            <h3 class="process-title">技术方案</h3>
            <div class="process-content">
              <div class="process-steps">
                <div
                  v-for="(step, index) in processSteps"
                  :key="index"
                  class="process-step"
                >
                  <div class="step-number">{{ index + 1 }}</div>
                  <div class="step-content">
                    <h4 class="step-title">{{ step.title }}</h4>
                    <p class="step-desc">{{ step.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div> -->
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

// 使用 require.context 让 webpack 在打包时收集网络安全详情图片资源
const securityDetailImageCtx = require.context('@/assets/security/img_detail', false, /\.(png|jpe?g)$/)

export default {
  name: 'SecurityDetail',
  data() {
    return {
      solutionDetail: null,
      loading: false,
      error: null
    }
  },
  computed: {
    solutionTitle() {
      return this.solutionDetail?.title || '网络安全解决方案'
    },
    solutionCategory() {
      return this.solutionDetail?.category || '网络安全'
    },
    updateDate() {
      return this.solutionDetail?.updatedAt || ''
    },
    solutionImage() {
      return this.resolveSolutionImage(this.solutionDetail?.image || '')
    },
    solutionDescription() {
      return this.solutionDetail?.intro || ''
    },
    features() {
      return this.solutionDetail?.highlights || []
    },
    scenariosIntro() {
      return '该解决方案广泛应用于以下场景，为企业构建全面的安全防护体系：'
    },
    scenarios() {
      return this.solutionDetail?.scenarios || []
    },
    processSteps() {
      // 如果有techStack，转换为流程步骤
      const techStack = this.solutionDetail?.techStack || []
      return techStack.map((tech, index) => ({
        title: `技术方案 ${index + 1}`,
        description: tech
      }))
    }
  },
  async mounted() {
    const securityId = this.$route.query.id
    if (securityId) {
      await this.fetchSecurityDetail(securityId)
    } else {
      this.error = '缺少网络安全ID参数'
      this.loading = false
    }
  },
  methods: {
    async fetchSecurityDetail(securityId) {
      try {
        this.loading = true
        this.error = null
        const response = await axios.get(`/api/security/detail/${securityId}`)
        if (response.data.code === 200) {
          this.solutionDetail = response.data.data
        } else {
          this.error = response.data.message || '获取网络安全详情失败'
          this.solutionDetail = null
        }
      } catch (error) {
        console.error('获取网络安全详情失败:', error)
        if (error.response && error.response.status === 404) {
          this.error = '网络安全详情不存在'
        } else {
          this.error = '网络请求失败，请检查服务器是否运行'
        }
        this.solutionDetail = null
      } finally {
        this.loading = false
      }
    },
    goBack() {
      this.$router.push('/security')
    },
    resolveSolutionImage(imagePath) {
      if (!imagePath) return ''
      // JSON 中配置的是 "@security_img_detail/filename" 或 "@img_detail/filename"
      if (imagePath.startsWith('@security_img_detail/')) {
        const fileName = imagePath.replace('@security_img_detail/', '')
        try {
          return securityDetailImageCtx('./' + fileName)
        } catch (e) {
          console.warn('网络安全详情图片未找到:', fileName, e)
          return ''
        }
      }
      if (imagePath.startsWith('@img_detail/')) {
        const fileName = imagePath.replace('@img_detail/', '')
        try {
          return securityDetailImageCtx('./' + fileName)
        } catch (e) {
          console.warn('网络安全详情图片未找到:', fileName, e)
          return ''
        }
      }
      // 其他情况直接返回原路径（例如外部 URL）
      return imagePath
    }
  }
}
</script>

<style scoped>
.solution-detail {
  min-height: 100vh;
  /* padding-top: 70px; */
  background: #f5f7fa;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 顶部标题区域 */
.products-header {
  position: relative;
  background-image: url('@/assets/background_header/网络安全详情页.png');
  background-size: cover;
  background-position: center;
  padding: 45px 0;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  align-items: center;
}

.products-header .container {
  position: relative;
  z-index: 2;
  width: 100%;
  margin: 0;
  padding: 0 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
}

.page-title {
  position: relative;
  z-index: 2;
  margin: 0;
  text-align: left;
  padding: 0;
}

.page-subtitle {
  margin: 8px 0 0 0;
  text-align: left;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
  line-height: 1.8;
  opacity: 1;
  max-width: 900px;
  margin-left: 2em;
  position: relative;
  z-index: 2;
  backdrop-filter: blur(5px);
}

.title-glow {
  display: inline-block;
  font-size: 46px;
  font-weight: 900;
  font-family: 'Arial Black', 'Microsoft YaHei', sans-serif;
  letter-spacing: 5px;
  color: #ffffff;
  filter: drop-shadow(0 0 10px rgba(96, 165, 250, 0.6));
  position: relative;
  backdrop-filter: blur(5px);
}

/* 主内容区域 */
.detail-main {
  background: #f5f7fa;
  padding: 40px 0 80px 0;
}

/* 返回按钮 */
.back-button {
  margin-bottom: 30px;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 14px;
  color: #4c95e3;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-back:hover {
  color: white;
  background: #4c95e3;
  border-color: #4c95e3;
}

.btn-back span {
  font-size: 18px;
}

/* 详情卡片 */
.detail-card {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* 详情头部 */
.detail-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.detail-title {
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 15px 0;
  line-height: 1.4;
}

.detail-meta {
  display: flex;
  align-items: center;
  gap: 20px;
  font-size: 14px;
  color: #666;
}

.detail-category {
  padding: 4px 12px;
  background: #f0f7ff;
  color: #4c95e3;
  border-radius: 4px;
  font-weight: 500;
}

.detail-date {
  color: #999;
}

/* 方案图片 */
.detail-image {
  width: 100%;
  margin-bottom: 40px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.detail-image img {
  width: 80%;
  height: auto;
  display: block;
}

/* 方案简介 */
.detail-intro {
  margin-bottom: 40px;
}

.intro-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #4c95e3;
  display: inline-block;
}

.intro-content {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin: 0;
}

/* 方案特点 */
.detail-features {
  margin-bottom: 40px;
}

.features-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #4c95e3;
  display: inline-block;
}

.features-content {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
}

.features-list {
  margin: 0;
  padding-left: 25px;
  color: #333;
}

.features-list li {
  margin-bottom: 10px;
  line-height: 1.8;
}

/* 应用场景 */
.detail-scenarios {
  margin-bottom: 40px;
}

.scenarios-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #4c95e3;
  display: inline-block;
}

.scenarios-content {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
}

.scenarios-content p {
  margin: 0 0 15px 0;
}

.scenarios-list {
  margin: 0;
  padding-left: 25px;
  color: #333;
}

.scenarios-list li {
  margin-bottom: 10px;
  line-height: 1.8;
}

/* 实施流程 */
.detail-process {
  margin-bottom: 0;
}

.process-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #4c95e3;
  display: inline-block;
}

.process-content {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
}

.process-steps {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.process-step {
  display: flex;
  gap: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #4c95e3;
  transition: all 0.3s ease;
}

.process-step:hover {
  background: #f0f7ff;
  transform: translateX(4px);
}

.step-number {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: #4c95e3;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.step-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .products-header {
    padding: 50px 0;
    min-height: 190px;
  }

  .title-glow {
    font-size: 32px;
    letter-spacing: 1.5px;
  }

  .products-header .container {
    padding: 0 24px;
  }

  .page-subtitle {
    margin-left: 1.5em;
    font-size: 14px;
  }

  .detail-main {
    padding: 30px 0 60px 0;
  }

  .detail-card {
    padding: 30px 20px;
  }

  .detail-title {
    font-size: 26px;
  }

  .detail-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .intro-title,
  .features-title,
  .scenarios-title,
  .process-title {
    font-size: 20px;
  }

  .intro-content,
  .features-content,
  .scenarios-content,
  .process-content {
    font-size: 15px;
  }

  .process-step {
    flex-direction: column;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .title-glow {
    font-size: 28px;
    letter-spacing: 1px;
  }

  .detail-card {
    padding: 20px 15px;
  }

  .detail-title {
    font-size: 22px;
  }
}

/* 加载和错误状态 */
.detail-loading,
.detail-error {
  text-align: center;
  padding: 60px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4c95e3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.detail-loading p {
  color: #666;
  font-size: 16px;
}

.detail-error {
  background: #ffecec;
  border-radius: 8px;
  padding: 30px;
}

.detail-error p {
  color: #c0392b;
  margin: 0 0 10px 0;
  font-size: 16px;
}

.error-hint {
  color: #999 !important;
  font-size: 14px !important;
}
</style>

